/**
 * Rotulos e textos do cadastro completo do corretor (P-COR-1).
 *
 * Os valores gravados ficam como o servidor os devolve; aqui ficam so os nomes que a
 * tela mostra, num lugar so.
 */

export const PERMISSAO_VER = 'corretores.corretor.view';
export const PERMISSAO_EDITAR = 'corretores.corretor.update';
export const PERMISSAO_EXCLUIR = 'corretores.corretor.delete';
export const PERMISSAO_EXPORTAR = 'corretores.corretor.export';
export const PERMISSAO_COMISSAO = 'corretores.comissao.update';
export const PERMISSAO_CONFIGURAR = 'corretores.configuracao.update';

/** Situacao do CNPJ na Receita (Situacao do CNPJ do OnPoint). */
export const situacoesDoCnpj = [
  { value: 'ativa', label: 'Ativa' },
  { value: 'suspensa', label: 'Suspensa' },
  { value: 'inapta', label: 'Inapta' },
  { value: 'baixada', label: 'Baixada' },
  { value: 'nula', label: 'Nula' },
];

export function rotuloDaSituacaoDoCnpj(valor: string | undefined): string {
  return situacoesDoCnpj.find((item) => item.value === valor)?.label ?? '';
}

/**
 * Situacao SUSEP. O OnPoint mostra Ativo e Inativo; o SeguraCore guarda a situacao
 * da consulta com quatro valores, e regular corresponde ao Ativo do OnPoint (decisao
 * 3 da secao 11.8 do plano 49, aberta).
 */
export const situacoesSusep = [
  { value: 'nao_consultado', label: 'Nao consultado' },
  { value: 'regular', label: 'Regular (Ativo)' },
  { value: 'irregular', label: 'Irregular (Inativo)' },
  { value: 'nao_encontrado', label: 'Nao encontrado' },
];

export const papeisDoSocio = [
  { value: 'socio', label: 'Socio' },
  { value: 'administrador', label: 'Administrador' },
  { value: 'representante', label: 'Representante' },
];

export const tiposDeResponsavel = [
  { value: 'financeiro', label: 'Financeiro' },
  { value: 'tecnico', label: 'Tecnico' },
  { value: 'outro', label: 'Outro' },
];

export function rotuloDoTipoDeResponsavel(valor: string): string {
  return tiposDeResponsavel.find((item) => item.value === valor)?.label ?? valor;
}

/** Tipo de nomeacao, da ficha do tomador. */
export const tiposDeNomeacao: Record<string, string> = {
  primeira_emissao: 'Primeira emissao',
  ccg: 'CCG',
  carta_nomeacao: 'Carta de nomeacao',
};

/** Acoes da trilha do corretor, com o nome que o historico mostra. */
const acoes: Record<string, string> = {
  'corretores.corretor.create': 'Cadastro',
  'corretores.corretor.onboarding': 'Cadastro pelo assistente',
  'corretores.corretor.update': 'Alteracao do cadastro',
  'corretores.corretor.transition': 'Mudanca de etapa',
  'corretores.corretor.delete': 'Exclusao',
  'corretores.socio.create': 'Inclusao de socio',
  'corretores.socio.update': 'Alteracao de socio',
  'corretores.socio.delete': 'Exclusao de socio',
  'corretores.responsavel.create': 'Inclusao de responsavel',
  'corretores.responsavel.update': 'Alteracao de responsavel',
  'corretores.responsavel.delete': 'Exclusao de responsavel',
  'corretores.endereco.create': 'Inclusao de endereco',
  'corretores.endereco.update': 'Alteracao de endereco',
  'corretores.endereco.delete': 'Exclusao de endereco',
  'corretores.documento.request': 'Documentos pedidos',
  // P-COR-3
  'corretores.digital.update': 'Corretor digital',
  'corretores.limite_consulta.create': 'Cadastro do limite de consultas',
  'corretores.limite_consulta.update': 'Alteracao do limite de consultas',
  'corretores.desbloqueio_consulta.update': 'Desbloqueio das consultas',
  'corretores.assessoria.broker_add': 'Inclusao na assessoria',
  'corretores.assessoria.broker_update': 'Comissao da assessoria',
  'corretores.assessoria.broker_remove': 'Retirada da assessoria',
};

export function rotuloDaAcao(acao: string): string {
  return acoes[acao] ?? acao;
}

/** Aviso das regras que o pacote deixou sem efeito, por falta de fonte homologada. */
export const AVISO_REGRAS_PENDENTES =
  'O limite de comissao, a validade do cadastro e o PEP dos socios sao registrados, mas nao travam nada: as regras da seguradora ainda nao foram homologadas.';

/** A aba Termos de Aceite le os aceites do termo de uso (P-USU-2) com esta permissao. */
export const PERMISSAO_VER_ACEITES = 'termos.aceite.view';

export const SEM_PERMISSAO_TERMOS =
  'Os aceites do termo de uso pedem a permissao de ver os termos de aceite.';

export const PENDENCIA_DATA_NOMEACAO =
  'Sem pedido de nomeacao aprovado: a data e a da aprovacao do pedido, e a corretora ligada pela edicao do cadastro do tomador nao tem data.';

// --- Assessoria, corretor digital e limite de consultas (P-COR-3) -----------------

export const PERMISSAO_DIGITAL = 'corretores.digital.update';
export const PERMISSAO_LIMITE_CONSULTA = 'corretores.limite_consulta.update';
export const PERMISSAO_DESBLOQUEIO_CONSULTA = 'corretores.desbloqueio_consulta.update';
export const PERMISSAO_ASSESSORIA_VER = 'corretores.assessoria.view';
export const PERMISSAO_ASSESSORIA_CRIAR = 'corretores.assessoria.create';
export const PERMISSAO_ASSESSORIA_EDITAR = 'corretores.assessoria.update';

export const periodosDoLimite = [
  { value: 'mensal', label: 'Mensal' },
  { value: 'diario', label: 'Diario' },
];

export function rotuloDoPeriodo(valor: string): string {
  return periodosDoLimite.find((item) => item.value === valor)?.label ?? valor;
}

export const PENDENCIA_DIGITAL =
  'O corretor digital e so uma marca: o que ele libera ou dispensa ainda nao foi definido pela seguradora, e nada muda no cadastro, na cotacao ou na emissao.';

export const PENDENCIA_LIMITE_CONSULTA =
  'Com o bloqueio automatico ligado, a consulta nova ao bureau e recusada quando o limite e atingido. Com ele desligado, o limite so informa. O limite tambem pode ser editado na administracao da plataforma.';

export const PENDENCIA_CONTAGEM_CONSULTA =
  'Contam as consultas a bureau feitas pela corretora ou para os tomadores que ela atende, a partir da ativacao deste controle. A consulta valida reaproveitada nao conta.';

export const PENDENCIA_DISPENSAS =
  'As dispensas sao guardadas e nao dispensam nada: a validacao de compliance e os documentos pendentes ainda dependem de regra da seguradora.';

export const PENDENCIA_COMISSAO_VALIDACAO =
  'A consulta so compara a taxa com o limite de comissao. O que acontece com a taxa acima do limite ainda depende de regra da seguradora, e nada e bloqueado.';

export const PENDENCIA_COMISSAO_ASSESSORIA =
  'A comissao da assessoria e so registrada: a base, o limite e quem aprova ainda nao foram definidos pela seguradora.';

export const PENDENCIA_SESSAO_ASSESSOR =
  'A sessao em nome da corretora e aberta pelo proprio assessor: ele entra pela tela de acesso da assessoria (/entrar/empresa/assessor) e escolhe a corretora. A seguradora nao abre sessao em nome da assessoria.';

/** Resultado da consulta da comissao, com o nome que a tela mostra. */
export const resultadosDaComissao: Record<string, string> = {
  dentro_do_limite: 'Dentro do limite',
  acima_do_limite: 'Acima do limite',
  sem_limite: 'Sem limite de comissao cadastrado',
};
